import React from 'react';
import { StyleSheet, View } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';

import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';

import HeroWorkHome from './HeroWorkHome';
import LivingPixelScene from './LivingPixelScene';
import {
  DisplayHeading,
  PrimaryCta,
  StoreBadges,
  useEntrance,
  useLanding,
} from './LandingKit';
import {
  EASE,
  LAYOUT,
  SERIF,
  fluid,
  isWeb,
  labelType,
} from './landingTokens';

/** Illustrated scenery and independent atmosphere layers give the hero depth. */
function HeroAtmosphere() {
  const { isDark, width } = useLanding();
  const stackedScene = width < 1100;
  return (
    <View pointerEvents="none" aria-hidden={isWeb ? true : undefined} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={[StyleSheet.absoluteFillObject, { backgroundColor: isDark ? '#111A17' : '#DCEEF0' }]}>
      <View style={{ position: 'absolute', left: -24, right: -24, bottom: -12, height: stackedScene ? 660 : '104%' }}>
        <LivingPixelScene />
        {stackedScene && <LinearGradient colors={['#DCEEF0', 'rgba(220,238,240,0)']} style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 240 }} />}
      </View>
      <LinearGradient
        colors={isDark ? ['rgba(9,20,16,.88)', 'rgba(9,20,16,.7)', 'rgba(9,20,16,.25)'] : ['rgba(235,247,245,.38)', 'rgba(235,247,245,.18)', 'rgba(255,250,239,0)']}
        locations={[0, 0.48, 1]}
        style={StyleSheet.absoluteFillObject}
      />
      <LinearGradient colors={isDark ? ['rgba(21,29,24,0)', '#151D18'] : ['rgba(240,243,236,0)', '#F0F3EC']} style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: stackedScene ? 60 : 120 }} />
    </View>
  );
}

/**
 * Masthead. Centered statement, then the product doing the thing the
 * statement claims.
 */
function LandingEditorialHero({ topInset = 0 }) {
  const { c, isXS, width, pad, display, prose } = useLanding();
  const entered = useEntrance();
  const heroInk = c.isDark ? '#C8E6D5' : '#164B35';

  const headline = {
    ...display({ min: 38, max: 72, leading: 1.12, tracking: -0.04 }),
    ...(isWeb ? { fontFamily: SERIF } : {}),
    fontStyle: 'italic',
  };
  const description = prose({ min: 16, max: 18, leading: 1.5, tracking: -0.008 });

  return (
    <View
      accessibilityRole={isWeb ? 'banner' : undefined}
      accessibilityLabel="Gab AI"
      style={{ width: '100%', alignSelf: 'stretch', alignItems: 'center', overflow: 'hidden', position: 'relative' }}
    >
      <HeroAtmosphere />
      <View
        style={{
          width: '100%',
          maxWidth: LAYOUT.max,
          paddingHorizontal: pad,
          paddingBottom: width < 1100 ? 300 : 160,
          paddingTop: (isXS ? topInset : 0) + fluid(width, 108, 144),
          alignItems: 'center',
        }}
      >
        <DisplayHeading
          tag="h1"
          weight="normal"
          entered={entered}
          type={headline}
          color={heroInk}
          delay={120}
          align="center"
          style={{ maxWidth: '100%' }}
          lines={[[ 'AI that works.' ]]}
        />
        <View
          style={{
            width: '100%',
            maxWidth: 480,
            alignItems: 'center',
            marginTop: fluid(width, 12, 16),
            ...(isWeb ? {
              opacity: entered ? 1 : 0,
              transform: [{ translateY: entered ? 0 : 16 }],
              transition: `opacity 800ms ${EASE} 380ms, transform 800ms ${EASE} 380ms`,
            } : {}),
          }}
        >
          <Text
            tag="p"
            weight="normal"
            style={{
              color: c.isDark ? 'rgba(232,243,236,0.82)' : '#294B42',
              ...description,
              textAlign: 'center',
              ...(isWeb ? { whiteSpace: 'pre-line', textWrap: 'balance' } : {}),
            }}
          >
            Everything you need to turn ideas into done.
          </Text>
        </View>

        {/* The product's own Work composer. Every click inside it leads to signup. */}
        <View style={{ width: '100%', marginTop: fluid(width, 28, 36) }}>
          <HeroWorkHome entered={entered} />
        </View>

        <View
          accessibilityLabel="Create with Gab AI Work Agent"
          style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', columnGap: isXS ? 18 : 28, rowGap: 12, marginTop: 22, maxWidth: 780, paddingHorizontal: isXS ? 16 : 24, paddingVertical: 14, borderRadius: 18, backgroundColor: c.isDark ? 'rgba(16,25,20,.94)' : 'rgba(255,252,245,.94)', borderWidth: 1, borderColor: c.rule, ...(isWeb ? { backdropFilter: 'blur(12px)' } : {}) }}
        >
          {[
            { icon: 'film', label: 'AI videos' },
            { icon: 'globe', label: 'Research' },
            { icon: 'file-export', label: 'Real files' },
            { icon: 'code', label: 'Websites & code' },
            { icon: 'gamepad', label: 'Video games' },
          ].map((item) => (
            <View key={item.label} style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
              <Icon name={item.icon} size={13} color={c.accent} />
              <Text weight="medium" style={{ color: c.ink, fontSize: isXS ? 12 : 13 }}>{item.label}</Text>
            </View>
          ))}
        </View>

        <View
          style={{
            width: '100%',
            alignItems: 'center',
            marginTop: fluid(width, 26, 34),
            ...(isWeb ? {
              opacity: entered ? 1 : 0,
              transition: `opacity 800ms ${EASE} 700ms`,
            } : {}),
          }}
        >
          <View style={{ alignSelf: isXS ? 'stretch' : 'center' }}>
            <PrimaryCta restrained label="Start free" full={isXS} />
          </View>

          <View style={{ width: '100%', maxWidth: 600, alignSelf: 'center', alignItems: 'center' }}>
          <View
            style={{
              alignItems: 'center',
              gap: 12,
              marginTop: fluid(width, 28, 36),
              marginBottom: fluid(width, 40, 64),
            }}
          >
            <Text weight="bold" style={{ color: c.inkSoft, ...labelType(9.5, 0.18), backgroundColor: c.isDark ? 'rgba(16,25,20,.94)' : 'rgba(255,252,245,.94)', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 12, overflow: 'hidden' }}>
              On your Mac. In your browser. On the go.
            </Text>
            <StoreBadges featuredMac showExtension scale={0.9} style={{ justifyContent: 'center' }} />
          </View>
          </View>
        </View>
      </View>
    </View>
  );
}

export default React.memo(LandingEditorialHero);
